import { useState, useCallback } from "react";
import { BlockStack, FormLayout, Text } from "@medentic/polaris-react";
import { CustomModal } from "@shared/ui";
import { useTranslation } from "react-i18next";
import { AgendaSwimlane, MultilingualText } from "@features/events/types";
import { GenerativeTextField } from "@shared/ui";

interface AddEditTrackModalProps {
  isOpen: boolean;
  onClose: () => void;
  track?: AgendaSwimlane;
  onSave: (track: AgendaSwimlane) => void;
  isEditMode?: boolean;
}

export default function AddEditTrackModal({
  isOpen,
  onClose,
  track,
  onSave,
  isEditMode = false,
}: AddEditTrackModalProps) {
  const { t } = useTranslation();
  const [title, setTitle] = useState<MultilingualText>(() => track?.title ?? {});
  const [description, _setDescription] = useState<MultilingualText>(
    () => track?.description ?? {}
  );
  const [isTitleValid, setIsTitleValid] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};
    const hasAnyTitleValue = Object.values(title).some((value) => value?.trim());

    if (!hasAnyTitleValue || !isTitleValid) {
      newErrors.title = t(
        "eventDetails.agenda.tracks.validation.titleRequired"
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [title, isTitleValid, t]);

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    const trackData: AgendaSwimlane = {
      title: title,
      description: description,
      sessions: track?.sessions || [],
    };

    onSave(trackData);
    onClose();
  };

  const handleCancel = () => {
    setErrors({});
    onClose();
  };

  return (
    <CustomModal
      open={isOpen}
      onClose={handleCancel}
      title={
        isEditMode
          ? t("eventDetails.agenda.tracks.editTrack")
          : t("eventDetails.agenda.tracks.addTrack")
      }
      size="large"
      primaryAction={{
        content: isEditMode ? t("common.update") : t("common.add"),
        onAction: handleSave,
      }}
      secondaryActions={[
        {
          content: t("common.cancel"),
          onAction: handleCancel,
        },
      ]}
    >
      <CustomModal.Section>
        <FormLayout>
          <BlockStack gap="400">
            <GenerativeTextField
              id="track-title"
              placementId="event.event.track.title"
              label={t("eventDetails.agenda.tracks.trackTitle")}
              value={title}
              onChange={(value) => setTitle(value)}
              type="textField"
              canGenerate={true}
              requiredIndicator
              validateAllLanguages={true}
              onValidationChange={setIsTitleValid}
            />

            {errors.title && (
              <Text as="p" tone="critical">
                {errors.title}
              </Text>
            )}
          </BlockStack>
        </FormLayout>
      </CustomModal.Section>
    </CustomModal>
  );
}
